import React from 'react'
import { Box, Typography } from '@mui/material'
import { muahangKiotLike } from '../../layout/muahangKiotTokens'

const ROW_H = 34
const ROW_GAP_PX = 6
const LABEL_W = '148px'
const FONT_PX = muahangKiotLike.kiotFontSizePx
const VALUE_H = ROW_H - 2

const fieldFont = {
  fontFamily: muahangKiotLike.kiotFontStack,
  fontSize: `${FONT_PX}px`,
  lineHeight: 1.35,
} as const

const fieldRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: `${LABEL_W} minmax(0, 1fr)` },
  columnGap: 1.5,
  alignItems: 'center',
  minWidth: 0,
  minHeight: ROW_H,
  height: ROW_H,
  width: '100%',
  boxSizing: 'border-box',
} as const

const labelCellSx = {
  ...fieldFont,
  color: muahangKiotLike.kiotTextMuted,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: ROW_H,
  display: 'flex',
  alignItems: 'center',
  lineHeight: `${VALUE_H}px`,
} as const

const valueUnderlineSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
  height: ROW_H,
  minHeight: ROW_H,
  borderBottom: '1px solid',
  borderColor: '#d0d5dd',
  boxSizing: 'border-box',
} as const

export type KiotExpandUnderlineFieldProps = {
  label: string
  value?: React.ReactNode
  emphasize?: boolean
  link?: boolean
  multiline?: boolean
  /** Độ rộng cột label (vd. `92px`). Mặc định 148px. */
  labelWidth?: string | number
}

/** Ô label + value gạch chân kiểu Kiot expand (read-only). */
export const KiotExpandUnderlineField: React.FC<KiotExpandUnderlineFieldProps> = ({
  label,
  value,
  emphasize,
  link,
  multiline,
  labelWidth = LABEL_W,
}) => {
  const empty =
    value == null ||
    value === '' ||
    value === '—' ||
    (typeof value === 'string' && value.trim() === '')
  const display = empty ? '—' : value
  const tall = Boolean(multiline && !empty)
  const labelCol = typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth

  return (
    <Box
      sx={{
        ...fieldRowSx,
        gridTemplateColumns: { xs: '1fr', sm: `${labelCol} minmax(0, 1fr)` },
        ...(tall ? { height: 'auto', minHeight: ROW_H, alignItems: 'flex-start' } : null),
      }}
    >
      <Typography sx={{ ...labelCellSx, alignSelf: tall ? 'flex-start' : 'center' }}>{label}</Typography>
      <Box
        sx={{
          ...valueUnderlineSx,
          alignItems: tall ? 'flex-start' : 'center',
          height: tall ? 'auto' : ROW_H,
          minHeight: ROW_H,
          py: tall ? '4px' : 0,
        }}
      >
        <Typography
          component="div"
          sx={{
            ...fieldFont,
            flex: 1,
            minWidth: 0,
            fontWeight: emphasize ? 700 : 600,
            color: link ? muahangKiotLike.linkBlue : muahangKiotLike.kiotTextPrimary,
            whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
            overflow: multiline ? 'visible' : 'hidden',
            textOverflow: multiline ? undefined : 'ellipsis',
            lineHeight: tall ? 1.45 : `${VALUE_H}px`,
            wordBreak: multiline ? 'break-word' : undefined,
          }}
        >
          {display}
        </Typography>
      </Box>
    </Box>
  )
}

export type KiotExpandUnderlineColumnItem = {
  label: string
  value?: React.ReactNode
  emphasize?: boolean
  link?: boolean
  multiline?: boolean
}

export type KiotExpandUnderlineInfoPanelProps = {
  /** Cột trái */
  left: KiotExpandUnderlineColumnItem[]
  /** Cột giữa (nếu có → layout 3 cột) */
  middle?: KiotExpandUnderlineColumnItem[]
  /** Cột phải */
  right: KiotExpandUnderlineColumnItem[]
}

/** Panel thông tin expand — 2 hoặc 3 cột field gạch chân như Kiot đặt hàng. */
export const KiotExpandUnderlineInfoPanel: React.FC<KiotExpandUnderlineInfoPanelProps> = ({
  left,
  middle,
  right,
}) => {
  const cols = middle != null ? [left, middle, right] : [left, right]
  const colCount = cols.length

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: colCount >= 3 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
          md: `repeat(${colCount}, minmax(0, 1fr))`,
        },
        columnGap: { xs: 2, md: colCount >= 3 ? 2.5 : 4 },
        rowGap: 0.75,
        alignItems: 'start',
        bgcolor: '#fff',
        px: 2.5,
        py: 1.75,
      }}
    >
      {cols.map((items, colIdx) => (
        <Box
          key={colIdx}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${ROW_GAP_PX}px`,
            minWidth: 0,
          }}
        >
          {items.map((item) => (
            <KiotExpandUnderlineField key={item.label} {...item} />
          ))}
        </Box>
      ))}
    </Box>
  )
}
